/**
 * UI Store - Manages global UI state (loading spinner + toast bridge)
 *
 * Toasts: Uses legacy AngularJS toastr when available (embedded mode),
 * falls back to react-toastify in standalone mode.
 */

import { hideLegacyLoader, showLegacyLoader } from '@/utils/legacyLoader';
import { toast } from 'react-toastify';
import { create } from 'zustand';

/**
 * Dedupe window: an identical toast (same type + message) fired within this
 * many ms of the last one is suppressed.
 *
 * Why: a single route often fires 7-8 parallel API requests (sidebar, work
 * order, property, counts, …). When the network or server is unreachable
 * EVERY one of those rejects with the same error, and the axios interceptor
 * calls showError() once per rejection — producing a stack of 7-8 identical
 * "Unable to connect to the server" toasts. Collapsing identical messages
 * inside a short window shows the user a single toast for one underlying
 * failure, regardless of how many requests were in flight.
 */
const TOAST_DEDUPE_MS = 4000;

/** Last-shown timestamp keyed by `${type}:${message}`. */
const recentToasts = new Map<string, number>();

/**
 * Delegates to legacy AngularJS toastr when available, falls back to react-toastify.
 * Deduplicates identical toasts fired within TOAST_DEDUPE_MS.
 */
const legacyToast = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
  const key = `${type}:${message}`;
  const now = Date.now();
  const last = recentToasts.get(key);
  if (last != null && now - last < TOAST_DEDUPE_MS) {
    // Identical toast already shown moments ago — suppress the duplicate.
    return;
  }
  recentToasts.set(key, now);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toastr = (window as any).toastr;
  if (toastr?.[type]) {
    toastr[type](message);
  } else {
    // `toastId` is a second line of defence: react-toastify itself refuses to
    // render a toast whose id is already on screen.
    toast[type](message, { toastId: key });
  }
};

interface UIStore {
  isLoading: boolean;
  // Ref-count of mounted SafeView iframe routes. When > 0 the global
  // StandaloneSpinner is suppressed: SafeView renders its own loading spinner
  // inside the iframe, and showing ours on top produces two spinners. Ref-counted
  // (not a boolean) so overlapping mount/unmount during navigation can't leave it
  // stuck. Managed via useSafeViewHost().
  safeViewHostCount: number;
  enterSafeViewHost: () => void;
  exitSafeViewHost: () => void;
  startLoading: (message?: string) => void;
  stopLoading: () => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

export const useUIStore = create<UIStore>((set) => ({
  isLoading: false,
  safeViewHostCount: 0,

  enterSafeViewHost: () => set((s) => ({ safeViewHostCount: s.safeViewHostCount + 1 })),
  exitSafeViewHost: () => set((s) => ({ safeViewHostCount: Math.max(0, s.safeViewHostCount - 1) })),

  startLoading: () => {
    set({ isLoading: true });
    showLegacyLoader();
  },

  stopLoading: () => {
    set({ isLoading: false });
    hideLegacyLoader();
  },

  showSuccess: (message) => legacyToast('success', message),
  showError: (message) => legacyToast('error', message),
  showWarning: (message) => legacyToast('warning', message),
  showInfo: (message) => legacyToast('info', message),
}));
